import { fetchPlaceByGeoCoords } from 'actions/PlaceActions'
import { fetchOffersById } from 'actions/OfferActions'
import { fetchOrders } from 'actions/OrderActions'
import { nonNullable, uniqueBy } from 'lib/array/arrayUtils'
import { getLatestCompletedReservation, haveOrdersBeenFetched, sortOrdersByDeparture } from 'lib/order/orderUtils'
import { useEffect, useMemo } from 'react'

import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getUpcomingOrders } from 'selectors/orderSelectors'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'

interface HotelOrderDataWithMaybePlace {
  place: App.Place | undefined | null
  coords: App.GeoCoords
  dates: { checkIn: string, checkOut: string }
  propertyName: string
}

interface HotelOrderData extends HotelOrderDataWithMaybePlace {
  place: App.Place
}

function useOrderExperienceCarousels(): Array<HotelOrderData> {
  const dispatch = useAppDispatch()

  const upcomingOrders = useAppSelector((state) => getUpcomingOrders(state))
  const ordersFetched = useAppSelector((state) => haveOrdersBeenFetched(state, 'upcoming'))
  const offers = useAppSelector((state) => state.offer.offers)
  const places = useAppSelector((state) => state.destination.coordsToPlace)

  useEffect(() => {
    if (!ordersFetched) {
      dispatch(fetchOrders({ status: 'upcoming' }))
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const orders = useMemo(() => {
    const hotelOrders = Object.values(upcomingOrders).filter(order => order.items.length > 0 || order.bedbankItems.length > 0)
    return sortOrdersByDeparture(hotelOrders)
  }, [upcomingOrders])

  useEffect(() => {
    const offerIds = orders.filter(order => order.items.length > 0).map(order => order.items[0].offerId)
    dispatch(fetchOffersById(offerIds))
  }, [orders, dispatch])

  // now find the property coordinates so we can find the matching place ID
  const orderData = useMemo(() => {
    return nonNullable(orders.map(order => {
      if (order.bedbankItems[0]) {
        const property = order.bedbankItems[0].offer.property
        return {
          latitude: property.latitude,
          longitude: property.longitude,
          propertyName: property.name,
          checkIn: order.bedbankItems[0].checkIn.format(ISO_DATE_FORMAT),
          checkOut: order.bedbankItems[0].checkOut.format(ISO_DATE_FORMAT),
        }
      } else {
        // we know it must have a hotel item as we already pre filtered
        const property = offers[order.items[0].offerId]?.property
        const item = getLatestCompletedReservation(order)
        if (property && item) {
          return {
            latitude: property.latitude,
            longitude: property.longitude,
            propertyName: property.name,
            checkIn: item.reservation.startDate,
            checkOut: item.reservation.endDate,
          }
        }
      }
    }))
  }, [orders, offers])

  useEffect(() => {
    orderData.forEach(coord => {
      dispatch(fetchPlaceByGeoCoords(coord.latitude, coord.longitude))
    })
  }, [orderData, dispatch])

  const uniqueHotelOrders = useMemo((): Array<HotelOrderData> => {
    const values = orderData.map<HotelOrderDataWithMaybePlace>(data => ({
      coords: { latitude: data.latitude, longitude: data.longitude },
      dates: { checkIn: data.checkIn, checkOut: data.checkOut },
      propertyName: data.propertyName,
      place: places[`${data.latitude}-${data.longitude}`],
    })).filter((val): val is HotelOrderData => !!val.place)

    return uniqueBy(values, val => val.place)
  }, [orderData, places])

  return uniqueHotelOrders
}

export default useOrderExperienceCarousels
